import { couponSaving, formatPrice, invoiceDeposit, invoiceTotal } from "@1-now/booking-core";

/**
 * The rows of the "Price details" sheet (1NOW-11155), read off the same invoice
 * the bill renders (designs/Common/(car-search-form)/invoice.tsx), field for
 * field. Nothing here prices anything: every amount is a server field, and the
 * Total is invoiceTotal(), the figure the charge bills. If the bill changes
 * which field a row reads, this is the one other place to change.
 */
export interface PriceRow {
  key: string;
  label: string;
  /** A second, smaller line: the rate, or what a fee is made of. */
  sub?: string;
  amount: number;
  /** A concession, drawn with a minus. */
  negative?: boolean;
}

export interface PriceDetails {
  /** The server could not price the trip: say why instead of drawing numbers. */
  error?: string;
  /** Rental charges: the car, protection, extras, anything else billed. */
  charges: PriceRow[];
  discounts: PriceRow[];
  /** Taxes and fees: tax, service fee, location fee. `total` is their sum. */
  fees: { total: number; rows: PriceRow[] };
  /** "Total (incl. tax)" when the invoice has tax, else "Total". */
  hasTax: boolean;
  total: number | null;
  /** The refundable deposit, shown on its own and never added to the total. */
  deposit: number | null;
}

const num = (v: unknown) => Number(v || 0);
const sum = (rows: any[] | undefined) =>
  (rows ?? []).reduce((s, r) => s + num(r?.total), 0);
/** A rate as the bill words it (invoice.tsx formatRateSubtitle): "$12.00 x Trip", or the server's own unit. */
const rateText = (rate: unknown) => {
  if (rate === undefined || rate === null || rate === "") return `$${formatPrice(0)} x Trip`;
  const formatted = formatPrice(rate as number | string);
  return /[a-zA-Z]/.test(String(rate)) || /[a-zA-Z]/.test(formatted) ? `$${formatted}` : `$${formatted} x Trip`;
};
const join = (rows: any[] | undefined) =>
  rows
    ?.map((r: any) => r?.description || r?.name)
    .filter(Boolean)
    .join(" • ") || undefined;

export const buildPriceDetails = (invoice: any, carName?: string): PriceDetails => {
  const empty: PriceDetails = {
    charges: [],
    discounts: [],
    fees: { total: 0, rows: [] },
    hasTax: false,
    total: null,
    deposit: null,
  };
  if (!invoice || Array.isArray(invoice)) return empty;
  if (invoice.error) return { ...empty, error: String(invoice.error) };

  const b = invoice.breakdown ?? {};
  const charges: PriceRow[] = [];

  const rate: string | undefined = b.fleet?.rate;
  charges.push({
    key: "car",
    label: carName || "Rental",
    // The bill's own fallback when the server sent no rate label. The label is Python's own print of the
    // rate ("5.0 x 3 days"), so its figure goes through formatPrice like every other amount: "$5 x 3 days".
    sub: rate
      ? `$${formatPrice(rate)}`
      : `$${formatPrice(invoice.avg_car_price_per_day) || "0.00"} x ${num(invoice.trip_days)}-${num(invoice.trip_days) === 1 ? "Day" : "Days"}`,
    amount: num(invoice.car_total),
  });
  const lines = (kind: "insurance" | "extras" | "other", rows: any[] | undefined) =>
    (rows ?? []).forEach((r: any, i: number) =>
      charges.push({
        key: `${kind}-${i}`,
        label: `${Number(r?.quantity) > 1 ? `${r.quantity}x ` : ""}${r?.name ?? ""}`,
        sub: rateText(r?.rate),
        amount: num(r?.total),
      }),
    );
  if (invoice.insurance_total !== undefined) lines("insurance", b.insurance);
  if (invoice.extras_total !== undefined) lines("extras", b.extras);
  lines("other", b.other);
  (b.tolls ?? []).forEach((r: any, i: number) =>
    charges.push({ key: `toll-${i}`, label: r?.name || "Toll", amount: num(r?.total) }),
  );

  const discounts: PriceRow[] = [];
  if (num(invoice.discount) > 0)
    discounts.push({ key: "discount", label: "Discount", amount: num(invoice.discount), negative: true });
  if (invoice.coupon_info?.name)
    discounts.push({
      key: "coupon",
      label: String(invoice.coupon_info.name),
      amount: couponSaving(invoice),
      negative: true,
    });

  const feeRows: PriceRow[] = [];
  const hasFee = b.booking_fee?.length > 0 || (invoice.booking_fee !== undefined && invoice.booking_fee !== null);
  if (hasFee)
    feeRows.push({
      key: "fee",
      label: "Service fee",
      sub: join(b.booking_fee),
      amount:
        invoice.booking_fee !== undefined && invoice.booking_fee !== null
          ? num(invoice.booking_fee)
          : sum(b.booking_fee),
    });
  const hasTax = b.taxes?.length > 0 && invoice.tax !== undefined;
  if (hasTax) feeRows.push({ key: "tax", label: "Tax", sub: join(b.taxes), amount: num(invoice.tax) });
  if (b.locations?.length > 0 && invoice.location_total !== undefined)
    feeRows.push({ key: "location", label: "Location fee", sub: join(b.locations), amount: num(invoice.location_total) });

  return {
    charges,
    discounts,
    fees: { total: feeRows.reduce((s, r) => s + r.amount, 0), rows: feeRows },
    hasTax,
    total: invoiceTotal(invoice),
    deposit: num(invoice.security_deposit_total) > 0 ? invoiceDeposit(invoice) ?? num(invoice.security_deposit_total) : null,
  };
};
